import { Alert, Box } from '@mui/material';
import type { Theme } from '@mui/material';
import React, { useRef, useState } from 'react';
import type { VideoMetadata } from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import { isVideoFile } from './useCardVideoUpload';
import { useMediaUploadZoneDrag } from './useMediaUploadZoneDrag';
import MediaUploadZoneContent from './MediaUploadZoneContent';
import MediaUploadZoneFileInput from './MediaUploadZoneFileInput';
import VideoMetadataPromptDialog from './VideoMetadataPromptDialog';

interface MediaUploadZoneProps {
  uploading: boolean;
  uploadError: string | null;
  onDismissError: () => void;
  /** Resolves to whether the upload succeeded, so a multi-file drop can stop at the first failure. */
  onUpload: (metadata: VideoMetadata, file: File) => Promise<boolean>;
}

const NOT_VIDEO_MESSAGE = 'Only video files can be added (MP4, MOV, WebM or MKV).';
const PRIMARY_MAIN = 'primary.main';

function mediaUploadZoneSx(over: boolean, uploading: boolean) {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
    px: 2,
    py: 2.5,
    textAlign: 'center',
    border: '2px dashed',
    borderColor: over ? PRIMARY_MAIN : 'divider',
    borderRadius: 2,
    bgcolor: over ? 'action.selected' : 'action.hover',
    color: over ? PRIMARY_MAIN : 'text.secondary',
    cursor: uploading ? 'default' : 'pointer',
    outline: 'none',
    transition: 'border-color 0.12s ease, background-color 0.12s ease',
    '&:hover': uploading ? undefined : { borderColor: PRIMARY_MAIN },
    '&:focus-visible': {
      boxShadow: (theme: Theme) => `0 0 0 2px ${theme.palette.primary.main}`,
    },
  } as const;
}

/**
 * Sidebar upload area: drop video files on it, or click it to choose them, and
 * they are added to the library only (nothing is attached to a slide). Each
 * video in turn asks for the title and description viewers will see; cancelling
 * the prompt skips that one file.
 */
export default function MediaUploadZone({
  uploading,
  uploadError,
  onDismissError,
  onUpload,
}: MediaUploadZoneProps) {
  const [queue, setQueue] = useState<File[]>([]);
  const [rejected, setRejected] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const addFiles = (picked: File[]) => {
    const videos = picked.filter(isVideoFile);
    setRejected(videos.length < picked.length);
    if (videos.length > 0) setQueue(videos);
  };

  const drag = useMediaUploadZoneDrag(uploading, addFiles);

  const confirm = async (title: string, description: string) => {
    const [file, ...rest] = queue;
    setQueue(rest);
    const ok = await onUpload({ publicTitle: title, publicDescription: description }, file);
    if (!ok) setQueue([]);
  };

  const openPicker = () => {
    if (!uploading && queue.length === 0) inputRef.current?.click();
  };

  const error = rejected ? NOT_VIDEO_MESSAGE : uploadError;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box
        role="button"
        tabIndex={0}
        aria-label="Add videos to the library — drop files here or click to choose"
        aria-disabled={uploading}
        onClick={openPicker}
        onKeyDown={(event) => drag.onKeyDown(event, openPicker)}
        onDragOver={drag.onDragOver}
        onDragLeave={drag.onDragLeave}
        onDrop={drag.onDrop}
        sx={mediaUploadZoneSx(drag.over, uploading)}
      >
        <MediaUploadZoneContent uploading={uploading} />
      </Box>
      <MediaUploadZoneFileInput inputRef={inputRef} onFiles={addFiles} />
      {error && (
        <Alert
          severity="error"
          onClose={() => {
            setRejected(false);
            onDismissError();
          }}
          sx={{ py: 0 }}
        >
          {error}
        </Alert>
      )}
      <VideoMetadataPromptDialog
        open={queue.length > 0}
        fileName={queue[0]?.name}
        onCancel={() => setQueue(queue.slice(1))}
        onConfirm={(title, description) => void confirm(title, description)}
      />
    </Box>
  );
}
